import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formMappings } from "../../../../config/formMappings";
import { formFields } from "../../../../services/form_fields";

export default function SpecialAccommodationsTable() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];

  // Get field keys from formMappings
  const firstNameFieldKey = formMapping?.fieldMappings?.firstName;
  const lastNameFieldKey = formMapping?.fieldMappings?.lastName;
  const emailFieldKey = formMapping?.fieldMappings?.email;
  const phoneFieldKey = formMapping?.fieldMappings?.phone;
  const specialAccommodationsFieldKey =
    formMapping?.fieldMappings?.["Special Accommodations"];

  // Helper function to get field value (handles both v1 and v2)
  const getFieldValue = (entry: any, fieldKey: string | undefined): any => {
    if (!fieldKey) return undefined;

    // Check if API service mapped it to human-readable name (v1)
    const humanReadableName = formFields[fieldKey]?.name;
    if (humanReadableName && entry.meta[humanReadableName]) {
      return entry.meta[humanReadableName];
    }

    // Use raw field key (v2)
    return entry.meta[fieldKey];
  };

  // Filter entries where special accommodations is not "No", "None", or "N/A"
  const specialAccommodations = entries
    .filter((entry) => {
      const accommodations = getFieldValue(
        entry,
        specialAccommodationsFieldKey
      );
      const accommodationsStr = Array.isArray(accommodations)
        ? accommodations[0]
        : accommodations || "";
      return (
        accommodationsStr &&
        !["no.", "no", "none", "n/a"].includes(accommodationsStr.toLowerCase())
      );
    })
    .map((entry) => {
      const firstName = getFieldValue(entry, firstNameFieldKey);
      const lastName = getFieldValue(entry, lastNameFieldKey);
      const email = getFieldValue(entry, emailFieldKey);
      const phone = getFieldValue(entry, phoneFieldKey);
      const accommodations = getFieldValue(
        entry,
        specialAccommodationsFieldKey
      );

      return {
        name: `${
          Array.isArray(firstName) ? firstName[0] : firstName || "N/A"
        } ${Array.isArray(lastName) ? lastName[0] : lastName || "N/A"}`,
        email: Array.isArray(email) ? email[0] : email || "N/A",
        phoneNumber: Array.isArray(phone) ? phone[0] : phone || "N/A",
        description: Array.isArray(accommodations)
          ? accommodations[0]
          : accommodations || "N/A",
      };
    });

  return (
    <div className="overflow-y-scroll no-scrollbar rounded-lg bg-white shadow col-span-4">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Phone Number
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Description
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {specialAccommodations.map((entry, idx) => (
            <tr key={idx}>
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                {entry.name}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a href={`mailto:${entry.email}`} className="hover:underline">
                  {entry.email}
                </a>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a
                  href={`tel:${entry.phoneNumber}`}
                  className="hover:underline"
                >
                  {entry.phoneNumber}
                </a>
              </td>
              <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal break-words">
                {entry.description}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
